import type { SupabaseClient } from '@supabase/supabase-js';

export type PeerProfile = {
  displayName: string | null;
  photoUrl: string | null;
};

type Listener = () => void;

export interface PeerProfilesModule {
  getDisplayName(userId: string): string | null;
  getPhotoUrl(userId: string): string | null;
  hasProfile(userId: string): boolean;
  getProfile(userId: string): PeerProfile | undefined;
  fetchProfilesFor(userIds: string[]): Promise<void>;
  fetchProfileFor(userId: string): Promise<void>;
  /** Fetch again every id whose last fetch failed or came back empty (a channel rebuild, a join). */
  retryMissingProfiles(): Promise<void>;
  clearProfiles(): void;
  onProfilesChange(listener: Listener): () => void;
}

/**
 * A failed or empty fetch is not retried for this long on its own (a position row arrives every few
 * seconds per friend). retryMissingProfiles() skips the wait.
 */
export const PROFILE_MISS_RETRY_MS = 30_000;

/**
 * A row that came back with no display name is an answer, just not a name yet: each repeat doubles the wait, up to
 * this. A friend with no name set was refetched every 30 s all night (2026-10-16 audit).
 */
export const PROFILE_NAMELESS_MAX_RETRY_MS = 10 * 60 * 1000;

interface ProfileMiss {
  at: number;
  /** Consecutive answers with no name (0 for a failed fetch or no row: those retry on the flat wait). */
  nameless: number;
}

function missRetryMs(miss: ProfileMiss): number {
  if (miss.nameless <= 0) {
    return PROFILE_MISS_RETRY_MS;
  }
  return Math.min(PROFILE_MISS_RETRY_MS * 2 ** (miss.nameless - 1), PROFILE_NAMELESS_MAX_RETRY_MS);
}

export function createPeerProfilesModule(
  client: SupabaseClient,
  now: () => number = Date.now,
): PeerProfilesModule {
  const profiles = new Map<string, PeerProfile>();
  /**
   * Ids whose fetch failed or returned no row, and when. Kept apart from `profiles`: a miss cached as
   * {displayName: null} for the whole session left a friend "Group member" all night, because a
   * friend approved a moment ago is not readable until the membership lands (2026-10-14 Turnstile).
   */
  const misses = new Map<string, ProfileMiss>();
  /**
   * The photo from a row that has no name yet. Dropping the whole row lost a photo we had (2026-10-16 audit);
   * kept apart from `profiles` so the id still reads as a miss (hasProfile false) and the name is fetched again.
   */
  const namelessPhotos = new Map<string, string>();
  const inflight = new Map<string, Promise<void>>();
  const listeners = new Set<Listener>();

  function notifyListeners(): void {
    listeners.forEach((cb) => cb());
  }

  async function doFetch(userIds: string[]): Promise<void> {
    let data:
      { id: string; display_name: string | null; profile_photo_url: string | null }[] | null = null;
    let error: unknown = null;
    try {
      ({ data, error } = await client
        .from('profiles')
        .select('id, display_name, profile_photo_url')
        .in('id', userIds));
    } catch (err) {
      error = err; // no signal: a miss, retried later
    }

    const at = now();
    if (error) {
      userIds.forEach((id) => misses.set(id, { at, nameless: misses.get(id)?.nameless ?? 0 }));
      return;
    }

    const returnedIds = new Set<string>();
    const namelessIds = new Set<string>();
    let changed = false;
    for (const row of data ?? []) {
      const displayName =
        typeof row.display_name === 'string' && row.display_name.trim() ? row.display_name : null;
      const photoUrl = (row.profile_photo_url ?? '').trim() || null;
      if (displayName === null) {
        // No name yet is a miss, not an answer - but the photo is real: keep it, and back off the name.
        namelessIds.add(row.id);
        if (photoUrl !== null && namelessPhotos.get(row.id) !== photoUrl) {
          namelessPhotos.set(row.id, photoUrl);
          changed = true;
        }
        continue;
      }
      profiles.set(row.id, { displayName, photoUrl });
      misses.delete(row.id);
      namelessPhotos.delete(row.id);
      returnedIds.add(row.id);
      changed = true;
    }

    for (const id of userIds) {
      if (namelessIds.has(id)) {
        misses.set(id, { at, nameless: (misses.get(id)?.nameless ?? 0) + 1 });
      } else if (!returnedIds.has(id)) {
        misses.set(id, { at, nameless: 0 });
      }
    }

    if (changed) {
      notifyListeners();
    }
  }

  return {
    getDisplayName(userId) {
      return profiles.get(userId)?.displayName ?? null;
    },

    getPhotoUrl(userId) {
      return profiles.get(userId)?.photoUrl ?? namelessPhotos.get(userId) ?? null;
    },

    hasProfile(userId) {
      return profiles.has(userId);
    },

    getProfile(userId) {
      return profiles.get(userId);
    },

    onProfilesChange(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    clearProfiles() {
      if (profiles.size === 0 && inflight.size === 0 && namelessPhotos.size === 0) {
        misses.clear();
        return;
      }
      profiles.clear();
      inflight.clear();
      misses.clear();
      namelessPhotos.clear();
      notifyListeners();
    },

    async fetchProfilesFor(userIds) {
      const at = now();
      const toFetch = [...new Set(userIds)].filter((id) => {
        if (!id || profiles.has(id) || inflight.has(id)) return false;
        const miss = misses.get(id);
        return miss === undefined || at - miss.at >= missRetryMs(miss);
      });
      if (toFetch.length === 0) {
        return;
      }

      const request = doFetch(toFetch);
      toFetch.forEach((id) => inflight.set(id, request));
      try {
        await request;
      } finally {
        toFetch.forEach((id) => inflight.delete(id));
      }
    },

    async retryMissingProfiles() {
      // Skip the wait, but keep the nameless count so the backoff resumes where it was.
      const ids = [...misses.keys()];
      for (const [id, miss] of misses) {
        misses.set(id, { ...miss, at: Number.NEGATIVE_INFINITY });
      }
      await this.fetchProfilesFor(ids);
    },

    async fetchProfileFor(userId) {
      if (profiles.has(userId)) {
        return;
      }
      const existing = inflight.get(userId);
      if (existing) {
        await existing;
        return;
      }
      await this.fetchProfilesFor([userId]);
    },
  };
}
